import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Logo from '@/components/feature/Logo';
import {
  defaultRadioSettings,
  statusOverviewData,
  type RadioSettingsData,
  type SaveStatus,
} from '@/mocks/adminSettingsRadio';
import { SwalAlert } from '@/hooks/useSweetAlert';
import StatusOverview from './components/StatusOverview';
import GeneralTab from './components/GeneralTab';
import SettingsStreamingTab from './components/SettingsStreamingTab';
import SettingsCabinaTab from './components/SettingsCabinaTab';
import SettingsCommunityTab from './components/SettingsCommunityTab';
import SettingsQRTab from './components/SettingsQRTab';
import SettingsDomainTab from './components/SettingsDomainTab';
import SettingsPaymentsTab from './components/SettingsPaymentsTab';
import SettingsChargesTab from './components/SettingsChargesTab';
import EntitlementsTab from './components/EntitlementsTab';
import SubscriptionTab from './components/SubscriptionTab';
import CheckoutReturn from '@/components/feature/CheckoutReturn';

const TABS = [
  { id: 'suscripcion', label: 'Suscripción', icon: 'ri-bill-line' },
  { id: 'general', label: 'General', icon: 'ri-settings-3-line' },
  { id: 'streaming', label: 'Streaming', icon: 'ri-broadcast-line' },
  { id: 'cabina', label: 'Cabina', icon: 'ri-chat-3-line' },
  { id: 'community', label: 'Community', icon: 'ri-group-line' },
  { id: 'qr', label: 'QR', icon: 'ri-qr-code-line' },
  { id: 'domain', label: 'Domain', icon: 'ri-global-line' },
  { id: 'premium', label: 'Premium', icon: 'ri-vip-diamond-line' },
  { id: 'payments', label: 'Payments', icon: 'ri-bank-card-line' },
  { id: 'charges', label: 'Charges', icon: 'ri-stack-line' },
] as const;

type TabId = typeof TABS[number]['id'];

export default function AdminSettings() {
  const [settings, setSettings] = useState<RadioSettingsData>(defaultRadioSettings);
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saveStatus, setSaveStatus] = useState<Record<TabId, SaveStatus>>(
    Object.fromEntries(TABS.map((t) => [t.id, 'idle' as SaveStatus])) as Record<TabId, SaveStatus>
  );
  const [showCheckoutReturn, setShowCheckoutReturn] = useState(false);

  // ── Checkout return detection ──
  useEffect(() => {
    // Detect ?checkout=success or ?checkout=cancelled in the URL
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const checkoutParam = params.get('checkout');
    if (checkoutParam === 'success' || checkoutParam === 'cancelled') {
      setShowCheckoutReturn(true);
      // Clean URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setPageState('ready'), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleSave = useCallback((tab: TabId) => {
    setSaveStatus((prev) => ({ ...prev, [tab]: 'saving' }));
    setTimeout(() => {
      setSaveStatus((prev) => ({ ...prev, [tab]: 'saved' }));
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => setSaveStatus((prev) => ({ ...prev, [tab]: 'idle' })), 1500);
    }, 600);
  }, []);

  const handlePreview = useCallback(() => {
    const code = settings.frecuencia.frecuenciaWeb || 'tucodigo';
    Swal.fire({
      title: 'Vista Previa',
      html: `<div style="text-align:left;font-size:13px"><p>Así se ve tu perfil público:</p><div style="background:#f3f4f6;padding:10px;border-radius:8px"><p style="font-weight:600;color:#111827;margin:0;font-size:12px"><i class="ri-global-line"></i> radiofrecuenciaweb.com/r/${code}</p></div></div>`,
      icon: 'info', confirmButtonText: 'Abrir vista previa', showCancelButton: true, cancelButtonText: 'Cerrar',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    }).then((r) => { if (r.isConfirmed) window.open(`https://radiofrecuenciaweb.com/r/${code}`, '_blank'); });
  }, [settings]);

  const handleCopyUrl = useCallback(async () => {
    const code = settings.frecuencia.frecuenciaWeb || 'tucodigo';
    const url = `https://radiofrecuenciaweb.com/r/${code}`;
    try { await navigator.clipboard.writeText(url); Swal.fire({ title: 'URL copiada', html: `<p style="font-size:13px"><code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:12px">${url}</code></p>`, icon: 'success', timer: 2000, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', htmlContainer: 'text-sm' } }); }
    catch { Swal.fire({ title: 'URL', html: `<p style="font-size:12px;word-break:break-all">${url}</p>`, icon: 'info', customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50' }, buttonsStyling: false }); }
  }, [settings]);

  const handleTestStream = useCallback(() => {
    navigate('/admin/settings/stream');
  }, [navigate]);

  const handleRetry = useCallback(() => { setPageState('loading'); setTimeout(() => setPageState('ready'), 600); }, []);
  const activeItem = TABS.find((t) => t.id === activeTab);

  return (
    <>
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <Logo size="sm" />
            <div><h1 className="text-foreground-950 font-bold text-sm">Configuración</h1><p className="text-foreground-400 text-[9px]">{activeItem?.label}</p></div>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"><i className="ri-arrow-left-line text-xs"></i>Volver</Link>
        </div>
      </header>

      {/* Desktop toolbar */}
      <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
        <div><h2 className="text-foreground-950 font-bold text-base">Configuración de la Radio</h2><p className="text-foreground-400 text-xs">Personalizá cada aspecto de tu estación</p></div>
        <div className="flex items-center gap-3">
          <button onClick={handlePreview} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-xs"></i>Vista previa</button>
          <button onClick={handleCopyUrl} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-file-copy-line text-xs"></i>Copiar URL</button>
          <button onClick={handleTestStream} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-play-circle-line text-xs"></i>Probar Stream</button>
          <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-external-link-line text-xs"></i>Ver sitio</Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6 lg:p-8 pb-8">
        {pageState === 'loading' && (
          <div className="max-w-[900px] mx-auto animate-pulse space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-24 bg-background-200 rounded-2xl"></div>))}</div>
            <div className="flex gap-2 overflow-x-auto pb-2">{Array.from({ length: 8 }).map((_, i) => (<div key={i} className="h-9 w-24 bg-background-200 rounded-full shrink-0"></div>))}</div>
            <div className="h-80 bg-background-200 rounded-2xl"></div>
          </div>
        )}

        {pageState === 'error' && (
          <div className="max-w-[900px] mx-auto">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4"><i className="ri-error-warning-line text-red-500 text-2xl"></i></div>
              <h3 className="text-foreground-950 font-bold text-sm mb-1">Error al cargar</h3>
              <p className="text-foreground-400 text-xs mb-4">No se pudieron obtener los datos.</p>
              <button onClick={handleRetry} className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-refresh-line mr-1.5"></i>Reintentar</button>
            </div>
          </div>
        )}

        {pageState === 'ready' && (
          <div className="max-w-[900px] mx-auto space-y-6">
            <StatusOverview status={statusOverviewData} onCopyUrl={handleCopyUrl} onDownloadQr={() => {}} onPreview={handlePreview} />

            <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
              {/* Tab bar */}
              <div className="flex items-center gap-1 border-b border-background-200 px-3 py-2 overflow-x-auto scrollbar-none">
                {TABS.map((tab) => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all ${activeTab === tab.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'}`}>
                    <i className={`${tab.icon} text-xs`}></i>{tab.label}
                    {saveStatus[tab.id] === 'saved' && <i className="ri-check-line text-emerald-400 text-xs animate-pulse ml-0.5"></i>}
                  </button>
                ))}
                {saveStatus[activeTab] === 'saving' && (
                  <span className="ml-auto text-foreground-400 text-[10px] flex items-center gap-1 shrink-0"><i className="ri-loader-4-line animate-spin text-xs"></i>Guardando...</span>
                )}
              </div>

              {/* Tab content */}
              <div className="p-5 lg:p-6">
                {activeTab === 'suscripcion' && <SubscriptionTab />}

                {activeTab === 'general' && (
                  <GeneralTab
                    general={settings.general}
                    branding={settings.branding}
                    contacto={settings.contacto}
                    redes={settings.redes}
                    apps={settings.apps}
                    onChangeGeneral={(general) => setSettings({ ...settings, general })}
                    onChangeBranding={(branding) => setSettings({ ...settings, branding })}
                    onChangeContacto={(contacto) => setSettings({ ...settings, contacto })}
                    onChangeRedes={(redes) => setSettings({ ...settings, redes })}
                    onChangeApps={(apps) => setSettings({ ...settings, apps })}
                    onSave={() => handleSave('general')}
                    saving={saveStatus.general === 'saving'}
                  />
                )}

                {activeTab === 'streaming' && (
                  <SettingsStreamingTab
                    frecuencia={settings.frecuencia}
                    onChangeFrecuencia={(frecuencia) => setSettings({ ...settings, frecuencia })}
                    onSave={() => handleSave('streaming')}
                    saving={saveStatus.streaming === 'saving'}
                    managedStreaming={settings.managedStreaming}
                    onManagedStreamingRefresh={() => {
                      setSettings({ ...settings });
                    }}
                  />
                )}

                {activeTab === 'cabina' && <SettingsCabinaTab />}

                {activeTab === 'community' && <SettingsCommunityTab />}

                {activeTab === 'qr' && <SettingsQRTab />}

                {activeTab === 'domain' && <SettingsDomainTab />}

                {activeTab === 'premium' && (
                  <EntitlementsTab
                    radioId={settings.frecuencia.frecuenciaWeb || 'nueva-actitud'}
                    radioName={settings.general.nombreRadio || 'Radio Nueva Actitud'}
                  />
                )}

                {activeTab === 'payments' && <SettingsPaymentsTab />}

                {activeTab === 'charges' && <SettingsChargesTab />}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Checkout Return Overlay */}
      {showCheckoutReturn && <CheckoutReturn />}

    </>
  );
}